// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React, { useCallback, useMemo } from 'react';
import { useAdaptedSelector } from '../hooks/useAdaptedSelector';
import { useHandlers } from '../hooks/useHandlers';
import { LocalDeviceSettings } from '../components/LocalDeviceSettings';
import { StartCallButton } from '../components/StartCallButton';
import { devicePermissionSelector } from '../selectors/devicePermissionSelector';
import { useSelector } from '../hooks/useSelector';
import { ActiveErrorMessage, DevicesButton, ErrorBar, useTheme } from '@internal/react-components';
import { getCallingSelector } from '@internal/calling-component-bindings';
import { Image, Panel, PanelType, Stack } from '@fluentui/react';
import { callDetailsContainerStyles, configurationSectionStyle, fillWidth, logoStyles, panelFocusProps, panelStyles, startCallButtonStyleDesktop } from '../styles/CallConfiguration.styles';
import { LocalPreview } from '../components/LocalPreview';
import { callDetailsStyleDesktop, callDetailsStyleMobile, configurationStackTokensDesktop, configurationStackTokensMobile, configurationContainerStyle, selectionContainerStyle, startCallButtonContainerStyleDesktop, startCallButtonContainerStyleMobile, startCallButtonStyleMobile, titleContainerStyleDesktop, titleContainerStyleMobile } from '../styles/CallConfiguration.styles';
import { useLocale } from '../../localization';
import { bannerNotificationStyles } from '../styles/CallPage.styles';
import { usePropsFor } from '../hooks/usePropsFor';
import { useAdapter } from '../adapter/CallAdapterProvider';
import { ConfigurationPageErrorBar } from '../components/ConfigurationPageErrorBar';
/* @conditional-compile-remove(video-background-effects) */
import { VIDEO_EFFECTS_SIDE_PANE_WIDTH_REM, useVideoEffectsPane } from '../components/SidePane/useVideoEffectsPane';
import { SidePane } from '../components/SidePane/SidePane';
import { SidePaneRenderer } from '../components/SidePane/SidePaneProvider';
/* @conditional-compile-remove(video-background-effects) */
import { useIsParticularSidePaneOpen } from '../components/SidePane/SidePaneProvider';
/* @conditional-compile-remove(video-background-effects) */
import { localVideoSelector } from '../../CallComposite/selectors/localVideoStreamSelector';
/* @conditional-compile-remove(capabilities) */
import { CapabilitiesChangeNotificationBarProps } from '../components/CapabilitiesChangedNotificationBar';

/**
 * @private
 */
export interface ConfigurationPageProps {
  mobileView: boolean;
  startCallHandler(): void;
  updateSidePaneRenderer: (renderer: SidePaneRenderer | undefined) => void;
  latestErrors: ActiveErrorMessage[];
  onDismissError: (error: ActiveErrorMessage) => void;
  modalLayerHostId: string;
  /* @conditional-compile-remove(capabilities) */
  capabilitiesChangedNotificationBarProps?: CapabilitiesChangeNotificationBarProps;
}

/**
 * @private
 */
export const ConfigurationPage = (props: ConfigurationPageProps): JSX.Element => {
  const {
    startCallHandler,
    mobileView,
    modalLayerHostId
  } = props;
  const theme = useTheme();
  const options = useAdaptedSelector(getCallingSelector(DevicesButton));
  const localDeviceSettingsHandlers = useHandlers(LocalDeviceSettings);
  const {
    video: cameraPermissionGranted,
    audio: microphonePermissionGranted
  } = useSelector(devicePermissionSelector);
  const errorBarProps = usePropsFor(ErrorBar);
  const adapter = useAdapter();
  const deviceState = adapter.getState().devices;
  let disableStartCallButton = !microphonePermissionGranted || deviceState.microphones?.length === 0;
  /* @conditional-compile-remove(video-background-effects) */
  const isCameraOn = useSelector(localVideoSelector).isAvailable;
  let filteredLatestErrors: ActiveErrorMessage[] = props.latestErrors;
  /* @conditional-compile-remove(video-background-effects) */
  if ((useIsParticularSidePaneOpen('videoeffects') || !isCameraOn) && errorBarProps) {
    filteredLatestErrors = filteredLatestErrors.filter(e => e.type !== 'unableToStartVideoEffect');
  }
  const locale = useLocale();
  const title = <Stack.Item className={mobileView ? titleContainerStyleMobile : titleContainerStyleDesktop} role="heading" aria-level={1}>
      {locale.strings.call.configurationPageTitle}
    </Stack.Item>;
  const callDescription = locale.strings.call.configurationPageCallDetails && <Stack.Item className={mobileView ? callDetailsStyleMobile : callDetailsStyleDesktop}>
      {locale.strings.call.configurationPageCallDetails}
    </Stack.Item>;
  let mobileWithPreview = mobileView; // When permission API is not available, we want to show screen saying checking for access (disappears on its own)
  // then based on permission setting, we show permission denied or nothing
  /* @conditional-compile-remove(video-background-effects) */
  const {
    toggleVideoEffectsPane,
    closeVideoEffectsPane,
    isVideoEffectsPaneOpen
  } = useVideoEffectsPane(props.updateSidePaneRenderer, mobileView, props.latestErrors, props.onDismissError);
  const startCall = useCallback(async () => {
    /* @conditional-compile-remove(video-background-effects) */
    closeVideoEffectsPane();
    startCallHandler();
  }, [startCallHandler, /* @conditional-compile-remove(video-background-effects) */closeVideoEffectsPane]);
  const panelLayerProps = useMemo(() => ({
    hostId: modalLayerHostId
  }), [modalLayerHostId]);
  const filteredErrorBarProps = useMemo(() => ({
    ...errorBarProps,
    activeErrorMessages: filteredLatestErrors
  }), [errorBarProps, filteredLatestErrors]);
  const containerStyles = useMemo(() => configurationContainerStyle(!mobileView), [mobileView]);
  return <Stack styles={containerStyles}>
      <Stack styles={bannerNotificationStyles}>
        <ConfigurationPageErrorBar errorBarProps={filteredErrorBarProps} onDismissError={props.onDismissError} />
      </Stack>
      {}

      {}

      <Stack verticalFill grow horizontal className={fillWidth}>
        <Stack className={fillWidth} verticalAlign="center" verticalFill={mobileWithPreview} tokens={mobileWithPreview ? configurationStackTokensMobile : configurationStackTokensDesktop}>
          <Stack.Item styles={callDetailsContainerStyles}>
            <Logo />
            {title}
            {callDescription}
          </Stack.Item>
          <Stack horizontal={!mobileWithPreview} horizontalAlign={mobileWithPreview ? 'stretch' : 'center'} verticalFill={mobileWithPreview} tokens={configurationStackTokensMobile}>
            {localPreviewTrampoline(mobileWithPreview)}
            <Stack styles={mobileView ? undefined : configurationSectionStyle}>
              {!mobileWithPreview && <Stack className={mobileView ? undefined : selectionContainerStyle(theme)}>
                  <LocalDeviceSettings {...options} {...localDeviceSettingsHandlers} cameraPermissionGranted={cameraPermissionGrantedTrampoline(cameraPermissionGranted)} microphonePermissionGranted={micPermissionGrantedTrampoline(microphonePermissionGranted)} /* @conditional-compile-remove(video-background-effects) */
              onClickVideoEffects={toggleVideoEffectsPane} />
                </Stack>}
              <Stack styles={mobileWithPreview ? startCallButtonContainerStyleMobile : startCallButtonContainerStyleDesktop} horizontalAlign={mobileWithPreview ? 'stretch' : 'end'}>
                <StartCallButton className={mobileWithPreview ? startCallButtonStyleMobile : startCallButtonStyleDesktop} onClick={startCall} disabled={disableStartCallButton} hideIcon={true} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Panel
      /* @conditional-compile-remove(video-background-effects) */ isOpen={isVideoEffectsPaneOpen} hasCloseButton={false} isBlocking={false} isHiddenOnDismiss={false} styles={panelStyles} focusTrapZoneProps={panelFocusProps} layerProps={panelLayerProps} type={PanelType.custom}
      /* @conditional-compile-remove(video-background-effects) */ customWidth={`${VIDEO_EFFECTS_SIDE_PANE_WIDTH_REM}rem`}>
          <SidePane mobileView={props.mobileView} updateSidePaneRenderer={props.updateSidePaneRenderer} />
        </Panel>
      </Stack>
    </Stack>;
};
const localPreviewTrampoline = (mobileView: boolean, doNotShow?: boolean): JSX.Element | undefined => {
  return <LocalPreview mobileView={mobileView} showDevicesButton={mobileView} />;
};
const cameraPermissionGrantedTrampoline = (cameraPermissionGranted: boolean | undefined, videoState?: PermissionState | 'unsupported' | undefined): boolean | undefined => {
  return cameraPermissionGranted;
};
const micPermissionGrantedTrampoline = (microphonePermissionGranted: boolean | undefined, audioState?: PermissionState | 'unsupported' | undefined): boolean | undefined => {
  return microphonePermissionGranted;
};
const Logo = (props: {
  logo?: {
    url: string;
    alt?: string;
    shape?: 'circle' | 'square';
  };
}): JSX.Element => {
  if (!props.logo) {
    return <></>;
  }
  return <Image styles={logoStyles(props.logo.shape ?? 'circle')} src={props.logo.url} alt={props.logo.alt} />;
};"../../../../../react-components/src""../../../../../calling-component-bindings/src"